import { TURNUS_EXTENSION_IDS } from "@/components/feature/Authenticaton/extensionAuthHandoff"

export type OnboardingBrowser = "chrome" | "edge" | "firefox" | "other"

/** Chromium browsers other than Edge report as "chrome": they install from the Chrome Web Store too. */
export const detectBrowser = (
  userAgent: string = navigator.userAgent,
): OnboardingBrowser => {
  if (userAgent.includes("Firefox/")) return "firefox"
  if (userAgent.includes("Edg/")) return "edge"
  if (userAgent.includes("Chrome/")) return "chrome"
  return "other"
}

const CHROME_WEB_STORE_URL = `https://chromewebstore.google.com/detail/turnusai/${TURNUS_EXTENSION_IDS[0]}`
const FIREFOX_ADD_ONS_URL =
  "https://addons.mozilla.org/firefox/addon/turnus-ai/"

// Edge installs from the Chrome Web Store until the Edge Add-ons listing is
// linked here: that build has the Chrome ID, so detection and sign-in work.
export const getExtensionStoreUrl = (
  browser: OnboardingBrowser,
): string | null => {
  if (browser === "chrome" || browser === "edge") return CHROME_WEB_STORE_URL
  if (browser === "firefox") return FIREFOX_ADD_ONS_URL
  return null
}

export interface ExtensionStatus {
  installed: boolean
  authenticated: boolean
}

const NOT_INSTALLED: ExtensionStatus = {
  installed: false,
  authenticated: false,
}
const RESPONSE_TIMEOUT_MS = 2000

interface ExternalRuntime {
  sendMessage: (
    extensionId: string,
    message: unknown,
    responseCallback: (response: unknown) => void,
  ) => void
  lastError?: { message?: string }
}

// Chrome and Edge expose chrome.runtime to this page only while an installed
// extension lists the page in externally_connectable; Firefox never does.
const getExternalRuntime = (): ExternalRuntime | undefined => {
  const runtime = (
    window as unknown as { chrome?: { runtime?: ExternalRuntime } }
  ).chrome?.runtime
  return typeof runtime?.sendMessage === "function" ? runtime : undefined
}

const ask = (runtime: ExternalRuntime, message: unknown): Promise<unknown> =>
  new Promise((resolve) => {
    const timer = window.setTimeout(
      () => resolve(undefined),
      RESPONSE_TIMEOUT_MS,
    )
    try {
      runtime.sendMessage(TURNUS_EXTENSION_IDS[0], message, (response) => {
        window.clearTimeout(timer)
        resolve(runtime.lastError ? undefined : response)
      })
    } catch {
      window.clearTimeout(timer)
      resolve(undefined)
    }
  })

/** Asks the extension (Chrome Web Store ID) whether it is installed and signed in. */
export const getExtensionStatus = async (): Promise<ExtensionStatus> => {
  const runtime = getExternalRuntime()
  if (!runtime) return NOT_INSTALLED

  const ping = (await ask(runtime, { type: "EXTENSION_DETECTION_PING" })) as
    | { installed?: boolean }
    | undefined
  if (ping?.installed !== true) return NOT_INSTALLED

  const auth = (await ask(runtime, { type: "GET_AUTH_STATUS" })) as
    | { authenticated?: boolean }
    | undefined
  return { installed: true, authenticated: auth?.authenticated === true }
}
